import {
  ACTIVE_STATUS, AVATAR_COLORS, DEFAULT_SLA_BADGE_CLASS, INACTIVE_STATUS,
  PANEL_MEMBER_FILTER_KEYS, PANEL_MEMBER_NAME_OVERRIDES, PANEL_MEMBER_ROLE_LABEL,
  UNNAMED_PANEL_MEMBER, SLA_BADGE_CLASSES
} from './panel_register_constants';
import { normalize_user_role } from '@/utils/system_constants/constant';
import { first_user_label, group_filter_key } from './panel_register_common_helpers';

const PANEL_FILTER_STRATEGIES = {
  all: all_panel_members
};

const PANEL_DETAIL_BUILDERS = [panel_role_details, panel_workload_details, panel_status_details, panel_contact_details];

export function map_role(role = {}) {
  const item = {};
  item.id = first_user_label([role.id, role.role_id]);
  item.role = first_user_label([role.role, role.role_name, role.name]);
  item.group_id = source_group_id(role);
  item.group_name = source_group_name(role);
  return item;
}

function source_group_id(source = {}) {
  const group = source.group || {};
  return first_user_label([source.group_id, group.id, source.group]);
}

function source_group_name(source = {}) {
  const group = source.group || {};
  return first_user_label([source.group_name, group.name]);
}

export function map_role_group(group = {}) {
  return {
    id: group.id || group.group_id,
    name: first_user_label([group.name, group.group_name, 'Unnamed group']),
    description: first_user_label([group.description])
  };
}

export function build_panel_filters(groups = []) {
  return [
    { key: 'all', label: 'All groups' },
    ...groups.map(panel_filter_from_group),
    { key: 'available', label: 'Available' },
    { key: 'at-capacity', label: 'At capacity' }
  ];
}

function panel_filter_from_group(group = {}) {
  return {
    key: group_filter_key(group.name),
    label: group.name
  };
}

export function map_panel_member(profile = {}, index = 0, roles = [], role_groups = []) {
  const context = panel_member_context(profile, index, roles, role_groups);
  return {
    ...panel_identity(context),
    ...panel_details(context)
  };
}

export function panel_member_is_allowed(member = {}) {
  const normalized_role = normalize_user_role(member.role);
  return Boolean(member.group_id) || normalized_role === 'System Admin';
}

function panel_member_context(profile = {}, index = 0, roles = [], role_groups = []) {
  const context = base_panel_member_context(profile, index);
  return {
    ...context,
    role_record: matching_role(context, roles),
    role_groups
  };
}

function base_panel_member_context(profile = {}, index = 0) {
  const user = panel_user(profile);
  const role = panel_role(profile, user);
  const role_name = panel_role_name(profile, user, role);
  const user_id = panel_user_id(profile, user);
  const profile_id = panel_profile_id(profile);
  const name = user_name(user);
  return { profile, user, role, role_name, user_id, profile_id, name, index };
}

function panel_user(profile = {}) {
  return first_user_label([profile.user, profile]);
}

function panel_role(profile = {}, user = {}) {
  return first_user_label([user.role, profile.role, {}]);
}

function panel_role_name(profile = {}, user = {}, role = {}) {
  return first_user_label([role.role, user.role_name, profile.role_name, PANEL_MEMBER_ROLE_LABEL]);
}

function panel_user_id(profile = {}, user = {}) {
  return first_user_label([profile.user_id, user.id]);
}

function panel_profile_id(profile = {}) {
  return first_user_label([profile.profile_id, profile.id]);
}

function panel_identity({ user, user_id, profile_id, name, index }) {
  return {
    id: user_id,
    user_id,
    profile_id,
    initials: initials_for_panel_user(name, user.email),
    avatar_color: AVATAR_COLORS[index % AVATAR_COLORS.length],
    name,
    code: panel_code(user_id, name),
    accredited: accredited_year(user.date_joined)
  };
}

function accredited_year(date_joined) {
  if (!date_joined) return 'Not recorded';
  return String(date_joined).slice(0, 4);
}

function panel_role_details({ profile, user, role, role_name, role_record, role_groups }) {
  const group = first_user_label([role.group, {}]);
  const group_name = role_group_name(profile, user, role, role_record, group);
  const assigned_scope_name = first_user_label([
    profile.role_group_name,
    profile.entity_type_name
  ]);
  const scope = role_is_system_admin(role_name)
    ? ''
    : first_user_label([
      assigned_scope_name,
      group_name,
      profile.project_name,
      profile.project,
      'Unassigned group'
    ]);

  return {
    role: role_name,
    role_id: first_user_label([role.id, user.role_id, profile.role_id]),
    scope,
    group_id: role_group_id(profile, role, role_record, group, scope, role_groups),
    group_key: group_filter_key(scope),
    specialism: first_user_label([profile.specialism, role_name])
  };
}

function panel_details(context) {
  const details = {};
  PANEL_DETAIL_BUILDERS.forEach((build_details) => {
    Object.assign(details, build_details(context));
  });
  return details;
}

function role_group_name(profile = {}, user = {}, role = {}, role_record = {}, group = {}) {
  return first_user_label([
    profile.role_group_name,
    role_record?.group_name,
    role.group_name,
    group.name,
    user.role_group_name
  ]);
}

function role_is_system_admin(role_name = '') {
  return normalize_user_role(role_name) === 'System Admin';
}

function role_group_id(profile = {}, role = {}, role_record = {}, group = {}, scope = '', role_groups = []) {
  const scope_group = role_groups.find((item) =>
    group_filter_key(item.name) === group_filter_key(scope)
  );

  return first_user_label([
    profile.role_group_id,
    scope_group?.id,
    role_record?.group_id,
    role.group_id,
    group.id
  ]);
}

function panel_workload_details({ profile }) {
  return {
    caseload: profile.caseload || '0 / 0',
    caseload_meta: profile.caseload_meta || 'No active caseload',
    sla: profile.sla || 'No SLA data',
    sla_badge_class: sla_badge_class(profile.sla)
  };
}

function panel_status_details({ profile, user }) {
  const status = status_for_member(profile, user);
  return {
    status: status.label,
    status_color: status.color,
    region_key: normalize_filter_key(profile.region_key),
    team_key: normalize_filter_key(profile.team_key),
    availability_key: status.key,
    is_active: is_active_member(profile, user)
  };
}

function panel_contact_details({ profile, user }) {
  return {
    first_name: first_user_label([user.first_name]),
    last_name: first_user_label([user.last_name]),
    email: first_user_label([user.email, profile.email]),
    phone_number: first_user_label([profile.phone_number, user.phone_number])
  };
}

export { filter_panel_members as filter_members };

function filter_panel_members(members, active_filter) {
  const selected_filter = panel_filter_key(active_filter);
  const strategy = panel_filter_strategy(selected_filter);
  return strategy(panel_member_list(members), selected_filter);
}

function panel_filter_strategy(active_filter) {
  return PANEL_FILTER_STRATEGIES[active_filter] || matching_panel_members;
}

function all_panel_members(members = []) {
  return members;
}

function matching_panel_members(members = [], active_filter = 'all') {
  return members.filter((member) => panel_member_filter_values(member).includes(active_filter));
}

function panel_member_filter_values(member = {}) {
  return PANEL_MEMBER_FILTER_KEYS.map((key) => member[key]);
}

function panel_member_list(members) {
  return Array.isArray(members) ? members : [];
}

function panel_filter_key(active_filter) {
  return active_filter || 'all';
}

function user_name(user = {}) {
  const values = [user_full_name(user), user.name, user.full_name, user.email, UNNAMED_PANEL_MEMBER];
  return first_user_label(values);
}

function user_full_name(user = {}) {
  return [user.first_name, user.last_name].filter(Boolean).join(' ').trim();
}

function initials_for_panel_user(name, email) {
  return panel_initials(initials_from_source(initials_source(name, email)));
}

function panel_initials(initials = '') {
  return first_user_label([initials, 'PM']);
}

function initials_from_source(source = '') {
  return String(source)
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0))
    .join('')
    .toUpperCase();
}

function initials_source(name = '', email = '') {
  return first_user_label([panel_initials_name(name), email]);
}

function panel_initials_name(name = '') {
  return panel_name_override(name, name);
}

function panel_name_override(name = '', fallback = '') {
  if (PANEL_MEMBER_NAME_OVERRIDES.has(name)) return PANEL_MEMBER_NAME_OVERRIDES.get(name);
  return fallback;
}

function panel_code(user_id, name = '') {
  if (user_id) {
    return `PM-${String(user_id).padStart(4, '0')}`;
  }

  return `PM-${initials_for_panel_user(name)}`;
}

function is_active_member(profile = {}, user = {}) {
  return profile.is_active === undefined || profile.is_active === null
    ? user.is_active !== false
    : profile.is_active !== false;
}

function status_for_member(profile = {}, user = {}) {
  return is_active_member(profile, user) ? ACTIVE_STATUS : INACTIVE_STATUS;
}

function normalize_filter_key(value) {
  return value ? group_filter_key(value) : 'all';
}

function matching_role({ role = {}, role_name = '', user = {}, profile = {} }, roles = []) {
  const role_id = first_user_label([role.id, user.role_id, profile.role_id]);
  return roles.find((item) => String(item.id) === String(role_id))
    || roles.find((item) => String(item.role).toLowerCase() === String(role_name).toLowerCase())
    || null;
}

function sla_badge_class(value) {
  const numeric = Number(String(value || '').replace('%', ''));
  if (!Number.isFinite(numeric)) return 'bg-light text-secondary border';

  const matched_class = SLA_BADGE_CLASSES.find((item) => numeric >= item.minimum);
  return matched_class?.class_name || DEFAULT_SLA_BADGE_CLASS;
}
